import React, { useState } from 'react';
import axios from 'axios';
import { getApiUrl } from '../config';
import './Setup.css';

const Setup = ({ onComplete }) => {
  const [step, setStep] = useState(1);
  const [config, setConfig] = useState({
    journalPath: '',
    transactionsPath: '',
    budgetPath: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleInputChange = (field, value) => {
    setConfig({ ...config, [field]: value });
    setError('');
  };

  const validateJournalPath = async () => {
    if (!config.journalPath) {
      setError('Please enter a journal file path');
      return false;
    }

    setLoading(true);
    try {
      // Check if file exists
      const response = await axios.post(getApiUrl('/api/file/exists'), {
        filePath: config.journalPath
      });

      if (!response.data.exists) {
        setError('Journal file not found at the specified path');
        setLoading(false);
        return false;
      }

      // Try to read accounts to validate it's a valid hledger file
      await axios.post(getApiUrl('/api/hledger/accounts'), {
        journalPath: config.journalPath
      });

      setLoading(false);
      return true;
    } catch (err) {
      setError('Invalid journal file or hledger error: ' + (err.response?.data?.error || err.message));
      setLoading(false);
      return false;
    }
  };

  const checkForBudgets = async () => {
    try {
      // Read the journal file to check for periodic transactions
      const response = await axios.post(getApiUrl('/api/file/read'), {
        filePath: config.journalPath
      });

      const content = response.data.content;
      const hasPeriodicTransactions = content.includes('~');

      return hasPeriodicTransactions;
    } catch (err) {
      return false;
    }
  };

  const handleNext = async () => {
    if (step === 1) {
      const isValid = await validateJournalPath();
      if (isValid) {
        setStep(2);
      }
    } else if (step === 2) {
      // Check for budgets in main journal
      const hasBudgets = await checkForBudgets();
      if (hasBudgets) {
        setStep(4); // Skip budget file prompt
      } else {
        setStep(3); // Ask for budget file
      }
    } else if (step === 3) {
      // Validate budget file if provided
      if (config.budgetPath) {
        setLoading(true);
        try {
          const response = await axios.post(getApiUrl('/api/file/exists'), {
            filePath: config.budgetPath
          });

          if (!response.data.exists) {
            setError('Budget file not found at the specified path');
            setLoading(false);
            return;
          }
        } catch (err) {
          setError('Error checking budget file: ' + err.message);
          setLoading(false);
          return;
        }
        setLoading(false);
      }
      setStep(4);
    } else if (step === 4) {
      onComplete(config);
    }
  };

  const handleSkip = () => {
    if (step === 3) {
      setStep(4);
    } else if (step === 4) {
      onComplete(config);
    }
  };

  return (
    <div className="setup">
      <div className="setup-container">
        <div className="setup-header">
          <h1>Welcome to hledger Dashboard</h1>
          <p>Let's get you set up in a few quick steps</p>
        </div>

        <div className="setup-progress">
          <div className={`progress-step ${step >= 1 ? 'active' : ''}`}>1</div>
          <div className="progress-line"></div>
          <div className={`progress-step ${step >= 2 ? 'active' : ''}`}>2</div>
          <div className="progress-line"></div>
          <div className={`progress-step ${step >= 3 ? 'active' : ''}`}>3</div>
          <div className="progress-line"></div>
          <div className={`progress-step ${step >= 4 ? 'active' : ''}`}>4</div>
        </div>

        <div className="setup-body">
          {step === 1 && (
            <div className="setup-step">
              <h2>Journal File Location</h2>
              <p className="step-description">
                Enter the path to your hledger journal file. This is typically a .journal or .ledger file.
              </p>
              <div className="form-group">
                <label className="form-label">Journal File Path</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="/path/to/your/journal.journal"
                  value={config.journalPath}
                  onChange={(e) => handleInputChange('journalPath', e.target.value)}
                  autoFocus
                />
                <p className="form-help">
                  Example: /home/user/finances/2024.journal
                </p>
              </div>
              {error && <div className="alert alert-danger">{error}</div>}
            </div>
          )}

          {step === 2 && (
            <div className="setup-step">
              <h2>Upcoming Transactions</h2>
              <p className="step-description">
                Would you like to track recurring transactions (bills, income, etc.)?
                This uses a separate JSON file to manage upcoming transactions.
              </p>
              <div className="form-group">
                <label className="form-label">Transactions File Path (Optional)</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="/path/to/transactions.json"
                  value={config.transactionsPath}
                  onChange={(e) => handleInputChange('transactionsPath', e.target.value)}
                />
                <p className="form-help">
                  Leave blank if you don't want to use this feature. You can set this up later.
                </p>
              </div>
              {error && <div className="alert alert-danger">{error}</div>}
            </div>
          )}

          {step === 3 && (
            <div className="setup-step">
              <h2>Budget File</h2>
              <p className="step-description">
                No budget entries found in your main journal. Do you have a separate file with periodic transactions (budgets)?
              </p>
              <div className="form-group">
                <label className="form-label">Budget File Path (Optional)</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="/path/to/directives.journal"
                  value={config.budgetPath}
                  onChange={(e) => handleInputChange('budgetPath', e.target.value)}
                />
                <p className="form-help">
                  This file should contain periodic transactions like "~ monthly from 2024-01"
                </p>
              </div>
              {error && <div className="alert alert-danger">{error}</div>}
            </div>
          )}

          {step === 4 && (
            <div className="setup-step">
              <h2>All Set!</h2>
              <p className="step-description">Your configuration is ready. Here's what you've set up:</p>
              <div className="config-summary">
                <div className="config-item">
                  <strong>Journal File:</strong>
                  <code>{config.journalPath}</code>
                </div>
                {config.transactionsPath && (
                  <div className="config-item">
                    <strong>Transactions File:</strong>
                    <code>{config.transactionsPath}</code>
                  </div>
                )}
                {config.budgetPath && (
                  <div className="config-item">
                    <strong>Budget File:</strong>
                    <code>{config.budgetPath}</code>
                  </div>
                )}
              </div>
              <p className="step-description">
                Click "Start Using Dashboard" to begin exploring your finances!
              </p>
            </div>
          )}
        </div>

        <div className="setup-footer">
          {step > 1 && step < 4 && (
            <button className="btn" onClick={() => setStep(step - 1)}>
              Back
            </button>
          )}
          <div style={{ flex: 1 }}></div>
          {(step === 2 || step === 3) && (
            <button className="btn" onClick={handleSkip}>
              Skip
            </button>
          )}
          <button
            className="btn btn-primary"
            onClick={handleNext}
            disabled={loading}
          >
            {loading ? (
              <>
                <div className="spinner"></div> Checking...
              </>
            ) : step === 4 ? (
              'Start Using Dashboard'
            ) : (
              'Next'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Setup;
